import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router';

// Hooks
import { useWorkspace } from 'Shared/utils/hooks/use-workspace';
import { useBannerTracking } from 'Shared/tracking/use-banner-tracking';
import { useAiSuggestionWorkspaceItems } from 'Shared/utils/hooks/use-ai-suggestion-workspace-items';
import { useWorkspaceAgendaAi } from 'Shared/utils/hooks/use-workspace-agenda-ai';

// Utils
import PageUrlsUtil from 'Shared/utils/PageUrls.utils';

// Components
import { WpDropdown, WpDropdownMenu, WpDropdownToggle, WpDropdownMenuItem } from 'Wpds/components/dropdown';
import { WpIcon } from 'Wpds/components/icon';
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import EmptyStateContainer from 'Shared/components/EmptyStateContainer/EmptyStateContainer';
import TypingDots from 'Shared/components/TypingDots/TypingDots';
import AgendaAiItem from './AgendaAiItem';
import OAuthButton from 'Shared/components/OAuthButton/OAuthButton';
import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import { WpText } from 'Wpds/components/text';
import { Illustrations, WpIllustration } from 'Wpds/components/illustration';

// Styles
import './AgendaAi.scss';

type Props = {
  categoryDropdown: React.ReactNode;
};

const AgendaAi = ({ categoryDropdown }: Props) => {
  const history = useHistory();
  const { id: workspaceId } = useWorkspace();

  const { trackPrimaryCTAClick: trackSelectChannelsCTA } = useBannerTracking({
    banner: 'Select Channels AgendaAI Announcement',
  });
  const { trackPrimaryCTAClick } = useBannerTracking({
    banner: 'Connect Slack AgendaAI Announcement',
  });

  const {
    isFetching,
    isRegenerating,
    suggestions,
    handleRefreshSuggestions,
    handleAcceptAiSuggestion,
  } = useAiSuggestionWorkspaceItems();

  const {
    slackAiOrganizationDataSourceId,
    selectedChannelIds,
    anyAiChatScanRequests,
    numOfActiveChannels,
  } = useWorkspaceAgendaAi({
    fetchAvailableChannels: false,
  });

  const OAuthRedirectUrl = `${PageUrlsUtil.makeWorkspaceSettingsUrl(workspaceId)}?show=agenda_ai`;

  const [channelIds, setChannelIds] = useState(selectedChannelIds);

  const showTopics = slackAiOrganizationDataSourceId && numOfActiveChannels > 0;
  const showEmptyState = suggestions.length === 0 && !isRegenerating;

  const handleRefreshClick = useCallback(() => {
    handleRefreshSuggestions();
  }, []);

  const handleSelectChannelsClick = useCallback(() => {
    trackSelectChannelsCTA();
    history.push({
      pathname: PageUrlsUtil.makeWorkspaceSettingsUrl(workspaceId),
      search: '?show=agenda_ai',
    });
  }, []);

  useEffect(() => {
    const isEqual =
      Array.isArray(channelIds) &&
      Array.isArray(selectedChannelIds) &&
      channelIds.length === selectedChannelIds.length &&
      channelIds.every((val, index) => val === selectedChannelIds[index]);

    if (
      showTopics &&
      ((!isEqual && !isFetching && !isRegenerating) ||
        (!anyAiChatScanRequests && !isFetching && !isRegenerating && suggestions.length === 0))
    ) {
      handleRefreshClick();
      setChannelIds(selectedChannelIds);
    }
  }, [showTopics, selectedChannelIds, isFetching, isRegenerating, suggestions, anyAiChatScanRequests]);

  const baseClassName = 'AgendaAi';

  return (
    <div className={baseClassName}>
      <div className={`${baseClassName}__toolbar`}>
        {categoryDropdown}
        {showTopics && (
          <WpButtonGroup>
            <WpDropdown placement="bottom-end" disabled={isRegenerating || isFetching}>
              <WpDropdownToggle>
                <WpButton variant="tertiary">
                  <WpIcon name="ellipsis-h-alt" />
                </WpButton>
              </WpDropdownToggle>
              <WpDropdownMenu>
                <WpDropdownMenuItem onClick={handleSelectChannelsClick}>
                  Edit connected Slack channels
                </WpDropdownMenuItem>
              </WpDropdownMenu>
            </WpDropdown>
            <WpButton onClick={handleRefreshClick} variant="secondary" loading={isRegenerating}>
              <WpIcon name="redo" />
              Refresh
            </WpButton>
          </WpButtonGroup>
        )}
      </div>
      {!slackAiOrganizationDataSourceId && (
        <WpContainer color="gray" className={`${baseClassName}__slack-setup-state`}>
          <WpContainerBody>
            <div className={`${baseClassName}__slack-setup-state__content`}>
              <WpIllustration
                src={Illustrations.slack_app_icon}
                className={`${baseClassName}__slack-setup-state__content__icon`}
              />
              <div className={`${baseClassName}__slack-setup-state__content__details`}>
                <WpText variant="bodyLg" bold noMargin>
                  Introducing Slack AgendaAI
                </WpText>
                <WpText noMargin color="gray-600">
                  Get suggested topics based off of your Slack discussions .
                </WpText>
                <OAuthButton
                  oauthProvider="slack"
                  text="Connect Slack"
                  variant="secondary"
                  color="primary"
                  block={false}
                  size="md"
                  iconType="icon"
                  redirectUrl={OAuthRedirectUrl}
                  onClick={trackPrimaryCTAClick}
                />
              </div>
            </div>
          </WpContainerBody>
        </WpContainer>
      )}
      {slackAiOrganizationDataSourceId && numOfActiveChannels === 0 && (
        <WpContainer color="gray" className={`${baseClassName}__slack-setup-state`}>
          <WpContainerBody>
            <div className={`${baseClassName}__slack-setup-state__content`}>
              <WpIllustration
                src={Illustrations.slack_app_icon}
                className={`${baseClassName}__slack-setup-state__content__icon`}
              />
              <div className={`${baseClassName}__slack-setup-state__content__details`}>
                <WpText variant="bodyLg" bold noMargin>
                  Use AgendaAI in this workspace
                </WpText>
                <WpText noMargin color="gray-600">
                  Connect relevant Slack channels to get suggested topics.
                </WpText>
                <WpButton variant="secondary" onClick={handleSelectChannelsClick}>
                  Choose Slack channels
                </WpButton>
              </div>
            </div>
          </WpContainerBody>
        </WpContainer>
      )}
      {showTopics && (
        <>
          {suggestions && (
            <>
              {suggestions.map(suggestion => (
                <AgendaAiItem
                  suggestion={suggestion}
                  key={suggestion.slug}
                  handleAcceptAiSuggestion={handleAcceptAiSuggestion}
                />
              ))}
            </>
          )}
          {isFetching && !isRegenerating && (
            <EmptyStateContainer subTitle="Fetching suggested discussion topics..." inContainer={false} padding={24} />
          )}
          {isRegenerating && !isFetching && (
            <div className={`${baseClassName}__generating`}>
              <div className={`${baseClassName}__generating__header`}>
                <TypingDots />
                <span className={`${baseClassName}__generating__header__label`}>
                  wrangling slack threads, give us a minute...
                </span>
              </div>
            </div>
          )}
          {!isFetching && showEmptyState && (
            <EmptyStateContainer
              subTitle="There aren’t any suggestions left"
              inContainer={false}
              padding={24}
              button={
                <WpButton variant="tertiary" onClick={handleRefreshClick} loading={isRegenerating}>
                  <WpIcon name="redo" /> Generate New Topics
                </WpButton>
              }
            />
          )}
        </>
      )}
    </div>
  );
};

export default AgendaAi;
